// Production-Ready Flashcards Viewer
// Features: Keyboard navigation, swipe gestures, study modes, spaced repetition, export

import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  RotateCcw, 
  Shuffle, 
  Check, 
  X,
  Keyboard,
  Download,
  Layers,
  Target,
  Clock,
  BookOpen,
  Filter,
  FileText,
  FileImage,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { FlashcardDeck, Flashcard } from '@/lib/content-studio/types';
import { useContentExporter } from '@/hooks/useContentExporter';

interface FlashcardsViewerProps {
  deck: FlashcardDeck;
  className?: string;
}

// Study modes
type StudyMode = 'all' | 'unknown-only' | 'spaced-repetition';

// Difficulty colors using semantic tokens
const DIFFICULTY_COLORS = {
  easy: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  hard: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

// Spaced repetition intervals (in review sessions)
const SR_INTERVALS = [1, 2, 4, 8, 16];

export function FlashcardsViewer({ deck, className }: FlashcardsViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [cards, setCards] = useState<Flashcard[]>(deck.cards);
  const [knownCards, setKnownCards] = useState<Set<string>>(new Set());
  const [unknownCards, setUnknownCards] = useState<Set<string>>(new Set());
  const [studyMode, setStudyMode] = useState<StudyMode>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [sessionStartTime] = useState<Date>(new Date());
  const [reviewCount, setReviewCount] = useState(0);
  
  // Export functionality
  const { exportToDocx, exportToPdf, isExporting } = useContentExporter();
  
  // Spaced repetition state
  const [cardIntervals, setCardIntervals] = useState<Map<string, number>>(new Map());
  const [lastReviewTimes, setLastReviewTimes] = useState<Map<string, Date>>(new Map());

  // Touch/swipe state
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);

  // Filtered cards based on mode and category
  const filteredCards = useMemo(() => {
    let filtered = [...cards];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(c => c.category === selectedCategory);
    }

    // Filter by study mode
    if (studyMode === 'unknown-only') {
      filtered = filtered.filter(c => unknownCards.has(c.id) || !knownCards.has(c.id));
    } else if (studyMode === 'spaced-repetition') {
      // Only show cards that are due for review
      const now = new Date();
      filtered = filtered.filter(c => {
        const lastReview = lastReviewTimes.get(c.id);
        const interval = cardIntervals.get(c.id) || 0;
        if (!lastReview) return true; // Never reviewed
        const hoursSinceReview = (now.getTime() - lastReview.getTime()) / (1000 * 60 * 60);
        return hoursSinceReview >= interval * 24; // interval is in days
      });
    }

    return filtered;
  }, [cards, selectedCategory, studyMode, knownCards, unknownCards, cardIntervals, lastReviewTimes]);

  const currentCard = filteredCards[currentIndex] || filteredCards[0];
  const totalCards = filteredCards.length;
  const progress = totalCards > 0 ? ((currentIndex + 1) / totalCards) * 100 : 0;

  // Session stats
  const sessionDuration = useMemo(() => {
    const now = new Date();
    const diffMs = now.getTime() - sessionStartTime.getTime();
    const minutes = Math.floor(diffMs / 60000);
    return minutes;
  }, [sessionStartTime, reviewCount]); // Update when reviewCount changes

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case ' ':
        case 'Enter':
          e.preventDefault();
          setIsFlipped(prev => !prev);
          break;
        case 'ArrowLeft':
        case 'h':
          e.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
        case 'l':
          e.preventDefault();
          goToNext();
          break;
        case 'ArrowUp':
        case 'k':
          e.preventDefault();
          markAsKnown();
          break;
        case 'ArrowDown':
        case 'j':
          e.preventDefault();
          markAsUnknown();
          break;
        case 's':
          e.preventDefault();
          shuffleCards();
          break;
        case 'r':
          e.preventDefault();
          resetProgress();
          break;
        case '?':
          setShowShortcuts(prev => !prev);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, totalCards]);

  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    });
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart) return;

    const deltaX = e.changedTouches[0].clientX - touchStart.x;
    const deltaY = e.changedTouches[0].clientY - touchStart.y;

    // Minimum swipe distance
    const minSwipe = 50;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Horizontal swipe
      if (deltaX > minSwipe) {
        goToPrevious();
      } else if (deltaX < -minSwipe) {
        goToNext();
      }
    } else {
      // Vertical swipe
      if (deltaY > minSwipe) {
        markAsUnknown();
      } else if (deltaY < -minSwipe) {
        markAsKnown();
      }
    }

    setTouchStart(null);
  };

  const goToNext = useCallback(() => {
    setIsFlipped(false);
    setTimeout(() => {
      if (currentIndex < totalCards - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setCurrentIndex(0);
        toast.success('Completed deck! Starting over...');
      }
    }, 150);
  }, [currentIndex, totalCards]);

  const goToPrevious = useCallback(() => {
    setIsFlipped(false);
    setTimeout(() => {
      if (currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      } else {
        setCurrentIndex(totalCards - 1);
      }
    }, 150);
  }, [currentIndex, totalCards]);

  const shuffleCards = () => {
    const shuffled = [...cards].sort(() => Math.random() - 0.5);
    setCards(shuffled);
    setCurrentIndex(0);
    setIsFlipped(false);
    toast.success('Cards shuffled!');
  };

  const resetProgress = () => {
    setCards(deck.cards);
    setCurrentIndex(0);
    setIsFlipped(false);
    setKnownCards(new Set());
    setUnknownCards(new Set());
    setCardIntervals(new Map());
    setLastReviewTimes(new Map());
    setReviewCount(0);
    toast.success('Progress reset');
  };

  const markAsKnown = useCallback(() => {
    if (!currentCard) return;
    
    setKnownCards((prev) => new Set([...prev, currentCard.id]));
    setUnknownCards((prev) => {
      const next = new Set(prev);
      next.delete(currentCard.id);
      return next;
    });

    // Spaced repetition: increase interval
    setCardIntervals(prev => {
      const next = new Map(prev);
      const currentInterval = prev.get(currentCard.id) || 0;
      const intervalIndex = SR_INTERVALS.indexOf(currentInterval);
      const newInterval = intervalIndex < SR_INTERVALS.length - 1 
        ? SR_INTERVALS[intervalIndex + 1] 
        : SR_INTERVALS[SR_INTERVALS.length - 1];
      next.set(currentCard.id, newInterval);
      return next;
    });
    setLastReviewTimes(prev => {
      const next = new Map(prev);
      next.set(currentCard.id, new Date());
      return next;
    });

    setReviewCount(prev => prev + 1);
    goToNext();
  }, [currentCard, goToNext]);

  const markAsUnknown = useCallback(() => {
    if (!currentCard) return;

    setUnknownCards((prev) => new Set([...prev, currentCard.id]));
    setKnownCards((prev) => {
      const next = new Set(prev);
      next.delete(currentCard.id);
      return next;
    });

    // Spaced repetition: reset interval
    setCardIntervals(prev => {
      const next = new Map(prev);
      next.set(currentCard.id, 1);
      return next;
    });
    setLastReviewTimes(prev => {
      const next = new Map(prev);
      next.set(currentCard.id, new Date());
      return next;
    });

    setReviewCount(prev => prev + 1);
    goToNext();
  }, [currentCard, goToNext]);

  // Export to CSV (Anki-compatible)
  const exportToAnki = () => {
    const csvContent = cards
      .map(card => `"${card.front.replace(/"/g, '""')}","${card.back.replace(/"/g, '""')}","${card.category || ''}"`)
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${deck.title.replace(/\s+/g, '-').toLowerCase()}-flashcards.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported for Anki!');
  };

  // Export to JSON
  const exportToJSON = () => {
    const exportData = {
      ...deck,
      exportedAt: new Date().toISOString(),
      progress: {
        known: Array.from(knownCards),
        unknown: Array.from(unknownCards),
        intervals: Object.fromEntries(cardIntervals),
      },
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${deck.title.replace(/\s+/g, '-').toLowerCase()}-flashcards.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as JSON!');
  };

  if (totalCards === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center p-8', className)}>
        <Layers className="w-12 h-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">
          {studyMode === 'unknown-only' 
            ? 'No cards to review! All cards are marked as known.'
            : studyMode === 'spaced-repetition'
            ? 'No cards due for review. Great job!'
            : 'No flashcards in this deck.'}
        </p>
        <Button variant="outline" size="sm" className="mt-4" onClick={resetProgress}>
          Reset Progress
        </Button>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className={cn('flex flex-col', className)}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold">{deck.title}</h3>
            <p className="text-sm text-muted-foreground">{deck.description}</p>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExporting}>
                  <Download className="h-4 w-4 mr-1" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover">
                <DropdownMenuItem onClick={() => exportToPdf('flashcards', deck)}>
                  <FileImage className="w-4 h-4 mr-2" />
                  PDF Document
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportToDocx('flashcards', deck)}>
                  <FileText className="w-4 h-4 mr-2" />
                  Word Document (.docx)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={exportToAnki}>
                  <BookOpen className="w-4 h-4 mr-2" />
                  Anki-compatible CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportToJSON}>
                  <Layers className="w-4 h-4 mr-2" />
                  JSON with progress
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={shuffleCards}>
              <Shuffle className="h-4 w-4 mr-1" />
              Shuffle
            </Button>
            <Button variant="outline" size="sm" onClick={resetProgress}>
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset
            </Button>
          </div>
        </div>

        {/* Study Mode & Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-muted-foreground" />
            <Select value={studyMode} onValueChange={(v) => {
              setStudyMode(v as StudyMode);
              setCurrentIndex(0);
            }}>
              <SelectTrigger className="w-[160px] h-8">
                <SelectValue placeholder="Study mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cards</SelectItem>
                <SelectItem value="unknown-only">Need Review</SelectItem>
                <SelectItem value="spaced-repetition">Spaced Repetition</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {deck.categories.length > 1 && (
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={selectedCategory} onValueChange={(v) => {
                setSelectedCategory(v);
                setCurrentIndex(0);
              }}>
                <SelectTrigger className="w-[140px] h-8">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {deck.categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => setShowShortcuts(!showShortcuts)}
              >
                <Keyboard className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Keyboard shortcuts (?)</p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Keyboard Shortcuts Panel */}
        {showShortcuts && (
          <Card className="mb-4 bg-muted/50">
            <CardContent className="py-3 px-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div><kbd className="px-1.5 py-0.5 bg-background rounded border text-xs">Space</kbd> Flip</div>
                <div><kbd className="px-1.5 py-0.5 bg-background rounded border text-xs">←</kbd> Previous</div>
                <div><kbd className="px-1.5 py-0.5 bg-background rounded border text-xs">→</kbd> Next</div>
                <div><kbd className="px-1.5 py-0.5 bg-background rounded border text-xs">↑</kbd> Know it</div>
                <div><kbd className="px-1.5 py-0.5 bg-background rounded border text-xs">↓</kbd> Review</div>
                <div><kbd className="px-1.5 py-0.5 bg-background rounded border text-xs">S</kbd> Shuffle</div>
                <div><kbd className="px-1.5 py-0.5 bg-background rounded border text-xs">R</kbd> Reset</div>
                <div><kbd className="px-1.5 py-0.5 bg-background rounded border text-xs">?</kbd> Toggle help</div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-muted-foreground">
              Card {currentIndex + 1} of {totalCards}
            </span>
            <div className="flex items-center gap-4 text-xs">
              <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                <Check className="h-3 w-3" />
                {knownCards.size} known
              </span>
              <span className="text-red-600 dark:text-red-400 flex items-center gap-1">
                <X className="h-3 w-3" />
                {unknownCards.size} to review
              </span>
              <span className="text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {sessionDuration}m
              </span>
            </div>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Flashcard */}
        <div
          className={cn(
            'relative min-h-[400px] cursor-pointer select-none',
            'touch-manipulation'
          )}
          onClick={() => setIsFlipped(!isFlipped)}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          style={{
            perspective: '1200px',
          }}
        >
          <div
            className={cn(
              'relative w-full h-full transition-transform duration-500',
              isFlipped && '[transform:rotateY(180deg)]'
            )}
            style={{
              transformStyle: 'preserve-3d',
            }}
          >
            {/* Front of card */}
            <Card
              className={cn(
                'absolute inset-0 flex flex-col items-center justify-center p-8',
                'backface-hidden shadow-lg rounded-xl',
                knownCards.has(currentCard?.id) && 'ring-2 ring-green-400/50',
                unknownCards.has(currentCard?.id) && 'ring-2 ring-red-400/50'
              )}
              style={{
                backfaceVisibility: 'hidden',
              }}
            >
              <div className="absolute top-3 left-3 flex items-center gap-2">
                <Badge className={DIFFICULTY_COLORS[currentCard?.difficulty || 'medium']}>
                  {currentCard?.difficulty}
                </Badge>
                {currentCard?.category && (
                  <Badge variant="outline">{currentCard.category}</Badge>
                )}
              </div>
              <p className="text-xl font-medium text-center px-6 leading-relaxed">{currentCard?.front}</p>
              <p className="text-sm text-muted-foreground mt-4">Tap to flip • Swipe to navigate</p>
            </Card>

            {/* Back of card */}
            <Card
              className={cn(
                'absolute inset-0 flex flex-col items-center justify-center p-8',
                'shadow-lg bg-primary/5 rounded-xl'
              )}
              style={{
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
              }}
            >
              <div className="absolute top-3 left-3">
                <Badge variant="secondary">Answer</Badge>
              </div>
              <p className="text-lg text-center px-6 leading-relaxed">{currentCard?.back}</p>
              {currentCard?.tags && currentCard.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-6 justify-center">
                  {currentCard.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Navigation and rating */}
        <div className="flex items-center justify-between mt-6">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={goToPrevious}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Previous (←)</TooltipContent>
          </Tooltip>

          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-950"
                  onClick={markAsUnknown}
                >
                  <X className="h-4 w-4 mr-1" />
                  Need to review
                </Button>
              </TooltipTrigger>
              <TooltipContent>Mark for review (↓)</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  className="border-green-200 text-green-600 hover:bg-green-50 dark:border-green-800 dark:hover:bg-green-950"
                  onClick={markAsKnown}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Got it
                </Button>
              </TooltipTrigger>
              <TooltipContent>Mark as known (↑)</TooltipContent>
            </Tooltip>
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={goToNext}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Next (→)</TooltipContent>
          </Tooltip>
        </div>

        {/* Stats bar */}
        <div className="mt-6 pt-4 border-t flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-4">
            <span>{reviewCount} reviews this session</span>
            {studyMode === 'spaced-repetition' && (
              <span className="text-primary">
                {filteredCards.length} cards due
              </span>
            )}
          </div>
          <div>
            {Math.round((knownCards.size / deck.cards.length) * 100)}% mastery
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

export default FlashcardsViewer;
